<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: { type: Object, default: null },
});

const emit = defineEmits(['save', 'close']);

const store = useStore();

const name = ref(props.agent?.name || '');
const description = ref(props.agent?.description || '');
const webhookUrl = ref(props.agent?.webhook_url || '');
const active = ref(props.agent?.active ?? true);
const selectedAccesses = ref(props.agent?.accesses || []);

// Headers (key-value pairs from config.headers)
const existingHeaders = props.agent?.config?.headers || {};
const headers = ref(
  Object.keys(existingHeaders).length
    ? Object.entries(existingHeaders).map(([k, v]) => ({ key: k, value: v }))
    : [{ key: '', value: '' }]
);

// Resource IDs from config
const selectedInboxIds = ref(props.agent?.config?.inbox_ids || []);
const selectedTeamIds = ref(props.agent?.config?.team_ids || []);
const selectedPipelineIds = ref(props.agent?.config?.pipeline_ids || []);
const mentionable = ref(props.agent?.config?.mentionable ?? true);

const agents = computed(() => store.getters['agents/getAgents']);
const teams = computed(() => store.getters['teams/getTeams']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const pipelines = computed(() => store.getters['crmPipelines/getPipelines'] || []);

const isEdit = computed(() => !!props.agent);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
  store.dispatch('crmPipelines/get');
});

const toggleAccess = (type, id) => {
  const idx = selectedAccesses.value.findIndex(
    a => a.accessible_type === type && a.accessible_id === id
  );
  if (idx !== -1) {
    selectedAccesses.value.splice(idx, 1);
  } else {
    selectedAccesses.value.push({ accessible_type: type, accessible_id: id });
  }
};

const isSelected = (type, id) => {
  return selectedAccesses.value.some(
    a => a.accessible_type === type && a.accessible_id === id
  );
};

const toggleId = (list, id) => {
  const idx = list.indexOf(id);
  if (idx !== -1) {
    list.splice(idx, 1);
  } else {
    list.push(id);
  }
};

const addHeader = () => {
  headers.value.push({ key: '', value: '' });
};

const removeHeader = idx => {
  headers.value.splice(idx, 1);
};

const handleSubmit = () => {
  if (!name.value.trim() || !webhookUrl.value.trim()) return;

  // Build headers object from key-value pairs
  const headersObj = {};
  headers.value.forEach(h => {
    const k = h.key.trim();
    if (k) headersObj[k] = h.value;
  });

  emit('save', {
    name: name.value.trim(),
    description: description.value.trim(),
    webhook_url: webhookUrl.value.trim(),
    active: active.value,
    config: {
      headers: headersObj,
      inbox_ids: selectedInboxIds.value,
      team_ids: selectedTeamIds.value,
      pipeline_ids: selectedPipelineIds.value,
      mentionable: mentionable.value,
    },
    accesses: selectedAccesses.value.map(a => ({
      accessible_type: a.accessible_type,
      accessible_id: a.accessible_id,
    })),
  });
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    @click.self="emit('close')"
  >
    <div
      class="mx-4 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-2xl"
    >
      <div class="flex items-center justify-between border-b border-n-weak px-6 py-4">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ isEdit ? 'Editar Agente IA' : 'Novo Agente IA' }}
        </h3>
        <button @click="emit('close')">
          <span class="i-lucide-x text-lg text-n-slate-10 hover:text-n-slate-12" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-1.5">
            <label
              for="ai-agent-name"
              class="text-sm font-medium text-n-slate-12"
            >
              Nome *
            </label>
            <input
              id="ai-agent-name"
              v-model="name"
              type="text"
              class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              placeholder="Ex: Assistente de Vendas"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              for="ai-agent-desc"
              class="text-sm font-medium text-n-slate-12"
            >
              Descrição
            </label>
            <textarea
              id="ai-agent-desc"
              v-model="description"
              rows="2"
              class="resize-none rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              placeholder="Breve descrição do agente IA"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              for="ai-agent-webhook"
              class="text-sm font-medium text-n-slate-12"
            >
              Webhook URL *
            </label>
            <input
              id="ai-agent-webhook"
              v-model="webhookUrl"
              type="url"
              class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              placeholder="https://seu-servidor.com/webhook"
            />
            <p class="text-xs text-n-slate-9">
              As mensagens serão enviadas via POST para esta URL.
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              Headers do Webhook
            </span>
            <p class="text-xs text-n-slate-9">
              Headers HTTP enviados na requisição (ex: Authorization, X-API-Key).
            </p>

            <div class="flex flex-col gap-2">
              <div
                v-for="(header, idx) in headers"
                :key="idx"
                class="flex flex-col gap-1.5 rounded-lg bg-n-alpha-1 p-3"
              >
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-medium uppercase tracking-wide text-n-slate-9">
                    Header {{ idx + 1 }}
                  </span>
                  <button
                    v-if="headers.length > 1"
                    class="text-[10px] text-n-slate-9 hover:text-red-500"
                    @click="removeHeader(idx)"
                  >
                    Remover
                  </button>
                </div>
                <input
                  v-model="header.key"
                  type="text"
                  class="rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 outline-none focus:border-n-brand"
                  placeholder="Nome (ex: Authorization)"
                />
                <input
                  v-model="header.value"
                  type="text"
                  class="rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 outline-none focus:border-n-brand"
                  placeholder="Valor (ex: Bearer sk-...)"
                />
              </div>
            </div>

            <button
              class="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-n-weak py-2 text-xs text-n-slate-10 transition-colors hover:border-violet-500 hover:text-violet-500"
              @click="addHeader"
            >
              + Adicionar header
            </button>
          </div>

          <div class="flex items-center justify-between rounded-lg border border-n-weak px-3 py-2.5">
            <span class="text-sm text-n-slate-12">Ativo</span>
            <button
              class="relative h-5 w-9 rounded-full transition-colors"
              :class="active ? 'bg-violet-600' : 'bg-n-slate-7'"
              @click="active = !active"
            >
              <span
                class="absolute left-0.5 top-0.5 size-4 rounded-full bg-white transition-transform"
                :class="active ? 'translate-x-4' : 'translate-x-0'"
              />
            </button>
          </div>

          <div class="flex items-center justify-between rounded-lg border border-n-weak px-3 py-2.5">
            <div>
              <span class="text-sm text-n-slate-12">Mencionável em conversas</span>
              <p class="text-xs text-n-slate-9">
                Permite que usuários com acesso mencionem este agente em notas privadas.
              </p>
            </div>
            <button
              class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
              :class="mentionable ? 'bg-violet-600' : 'bg-n-slate-7'"
              @click="mentionable = !mentionable"
            >
              <span
                class="absolute left-0.5 top-0.5 size-4 rounded-full bg-white transition-transform"
                :class="mentionable ? 'translate-x-4' : 'translate-x-0'"
              />
            </button>
          </div>

          <hr class="border-n-weak" />

          <div class="flex flex-col gap-3">
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                Recursos acessíveis
              </span>
              <p class="mt-0.5 text-xs text-n-slate-9">
                Selecione quais inboxes, times e pipelines o agente pode acessar.
                Os IDs selecionados serão enviados no payload do webhook.
              </p>
            </div>

            <div v-if="inboxes.length > 0" class="flex flex-col gap-1.5">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                Inboxes
              </span>
              <div class="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                <button
                  v-for="inbox in inboxes"
                  :key="`inbox-${inbox.id}`"
                  class="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    selectedInboxIds.includes(inbox.id)
                      ? 'border-violet-500 bg-violet-500/10 text-violet-700'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleId(selectedInboxIds, inbox.id)"
                >
                  {{ inbox.name }}
                </button>
              </div>
            </div>

            <div v-if="teams.length > 0" class="flex flex-col gap-1.5">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                Times
              </span>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="team in teams"
                  :key="`rteam-${team.id}`"
                  class="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    selectedTeamIds.includes(team.id)
                      ? 'border-violet-500 bg-violet-500/10 text-violet-700'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleId(selectedTeamIds, team.id)"
                >
                  {{ team.name }}
                </button>
              </div>
            </div>

            <div v-if="pipelines.length > 0" class="flex flex-col gap-1.5">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                Pipelines
              </span>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="pipeline in pipelines"
                  :key="`pipe-${pipeline.id}`"
                  class="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    selectedPipelineIds.includes(pipeline.id)
                      ? 'border-violet-500 bg-violet-500/10 text-violet-700'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleId(selectedPipelineIds, pipeline.id)"
                >
                  {{ pipeline.name }}
                </button>
              </div>
            </div>
          </div>

          <hr class="border-n-weak" />

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">Controle de acesso</span>
            <p class="text-xs text-n-slate-9">
              Selecione quais agentes e/ou times terão acesso a este agente IA.
            </p>

            <div v-if="teams.length > 0" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                Times
              </span>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="team in teams"
                  :key="`team-${team.id}`"
                  class="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    isSelected('Team', team.id)
                      ? 'border-violet-500 bg-violet-500/10 text-violet-700'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleAccess('Team', team.id)"
                >
                  {{ team.name }}
                </button>
              </div>
            </div>

            <div v-if="agents.length > 0" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                Agentes
              </span>
              <div class="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                <button
                  v-for="agentUser in agents"
                  :key="`user-${agentUser.id}`"
                  class="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    isSelected('User', agentUser.id)
                      ? 'border-violet-500 bg-violet-500/10 text-violet-700'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleAccess('User', agentUser.id)"
                >
                  {{ agentUser.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 border-t border-n-weak px-6 py-3">
        <Button
          size="sm"
          variant="ghost"
          color-scheme="secondary"
          label="Cancelar"
          @click="emit('close')"
        />
        <Button
          size="sm"
          variant="solid"
          color-scheme="primary"
          :label="isEdit ? 'Salvar' : 'Criar'"
          :disabled="!name.trim() || !webhookUrl.trim()"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>
